
import React, { useState } from 'react';

export const SellMyApartmentHero: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    propertyType: '',
    unitsCount: '',
    currentSituation: '',
    timeline: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone || !formData.propertyAddress) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    // Validate character limit for additionalInfo
    if (formData.additionalInfo && formData.additionalInfo.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formDataToSubmit = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5qlepr592clg457kek0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          propertyType: '',
          unitsCount: '',
          currentSituation: '',
          timeline: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  const remainingChars = 500 - formData.additionalInfo.length;

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/b1c2ad3971058c6e39f21affbd44ccb5.jpg"
          alt="Sell My Apartment Building"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center text-white mb-12">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
              <span className="block text-orange-400">Sell My Apartment Building</span>
              <span className="block text-4xl md:text-5xl mt-4">Fast for Cash</span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed">
              Need to sell your apartment building quickly? We buy apartments with tenants, vacant complexes, 
              and distressed properties nationwide. Get a fair cash offer in 24 hours and close in as little as 7 days.
            </p>
          </div>

          {/* Cash Offer Form */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 lg:p-12">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Get Your Cash Offer Now
                </h2>
                <p className="text-gray-600">
                  Fill out the form below and we'll contact you within 24 hours with a cash offer
                </p>
              </div>

              <form onSubmit={handleSubmit} id="sell-apartment-form" data-readdy-form className="space-y-6">
                {/* Personal Information */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                      When do you want to sell?
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                    >
                      <option value="">Select timeline</option>
                      <option value="ASAP">As soon as possible</option>
                      <option value="1-3 months">Within 1-3 months</option>
                      <option value="3-6 months">Within 3-6 months</option>
                      <option value="6-12 months">Within 6-12 months</option>
                      <option value="just-exploring">Just exploring options</option>
                    </select>
                  </div>
                </div>

                {/* Property Information */}
                <div>
                  <label htmlFor="propertyAddress" className="block text-sm font-medium text-gray-700 mb-2">
                    Property Address *
                  </label>
                  <input
                    type="text"
                    id="propertyAddress"
                    name="propertyAddress"
                    value={formData.propertyAddress}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                    placeholder="123 Main Street, City, State, ZIP"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 mb-2">
                      Property Type
                    </label>
                    <select
                      id="propertyType"
                      name="propertyType"
                      value={formData.propertyType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                    >
                      <option value="">Select property type</option>
                      <option value="Garden Apartments">Garden Apartments</option>
                      <option value="Mid-Rise Building">Mid-Rise Building</option>
                      <option value="High-Rise Tower">High-Rise Tower</option>
                      <option value="Mixed-Use Property">Mixed-Use Property</option>
                      <option value="Student Housing">Student Housing</option>
                      <option value="Senior Community">Senior Community</option>
                      <option value="Commercial Building">Commercial Building</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="unitsCount" className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Units
                    </label>
                    <input
                      type="number"
                      id="unitsCount"
                      name="unitsCount"
                      value={formData.unitsCount}
                      onChange={handleInputChange}
                      min="1"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      placeholder="e.g. 24"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="currentSituation" className="block text-sm font-medium text-gray-700 mb-2">
                    Current Situation
                  </label>
                  <select
                    id="currentSituation"
                    name="currentSituation"
                    value={formData.currentSituation}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  >
                    <option value="">Select situation</option>
                    <option value="Need quick sale">Need quick sale</option>
                    <option value="Property needs repairs">Property needs repairs</option>
                    <option value="High vacancy">High vacancy</option>
                    <option value="Inherited property">Inherited property</option>
                    <option value="Relocating">Relocating</option>
                    <option value="Financial difficulties">Financial difficulties</option>
                    <option value="Want to liquidate">Want to liquidate</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                    placeholder="Tell us more about your property or situation..."
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {remainingChars} characters remaining
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting || remainingChars < 0}
                    className={`px-12 py-4 rounded-lg font-bold text-lg transition-all duration-200 whitespace-nowrap ${
                      isSubmitting || remainingChars < 0
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-orange-500 hover:bg-orange-600 hover:scale-105'
                    } text-white shadow-lg`}
                  >
                    {isSubmitting ? 'Submitting...' : 'Get My Cash Offer'}
                    {!isSubmitting && <i className="ri-arrow-right-line ml-2"></i>}
                  </button>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="text-center p-4 bg-green-100 text-green-700 rounded-lg">
                    <i className="ri-checkbox-circle-fill mr-2"></i>
                    Thank you! We've received your information and will contact you within 24 hours with your cash offer.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="text-center p-4 bg-red-100 text-red-700 rounded-lg">
                    <i className="ri-error-warning-fill mr-2"></i>
                    Please check all required fields and try again. Make sure additional information is under 500 characters.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
